import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link as MuiLink,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { Link, useNavigate } from "react-router-dom";
import ThemeToggle from "../../ThemeToggle/ThemeToggle";
import { useState, useEffect } from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useTheme } from "../../../context/ThemeContext";
import { deleteAllCookies, getLoginSecured } from "../../../utils/common";
import { useSnackbar } from "notistack";
import { useTracking } from "../../../hooks/useTracking";
import {
  authxAppsBase,
  getLoginVerificationRedirect,
  isAuthxLoginSessionUsable,
  loginAuthxHeadersPreSession,
  normalizeAuthxSession,
  persistAuthxSessionCookies,
  persistPendingVerificationState,
} from "../../../utils/authx-login-helpers";
import {
  getAuthxAppsErrorMessage,
  parseAuthxAppsResponseBody,
} from "../../../utils/authx-apps-response";
import { getSafeRedirectUrl } from "../../../utils/navigation-utils";

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .email("Invalid email format")
    .required("Email is required"),
  password: Yup.string().required("Password is required"),
  rememberMe: Yup.boolean(),
});

const LoginPanel = () => {
  const { trackLoginAttempt, trackLoginSuccess, trackButtonClick } =
    useTracking();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState("");
  const { enqueueSnackbar } = useSnackbar();

  const [redirectBackTo, setRedirectBackTo] = useState("");
  const [open, setOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setOpen(false);
  };

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
      rememberMe: false,
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      trackLoginAttempt();
      deleteAllCookies();
      setOpen(false);
      try {
        const loginSecured = await getLoginSecured(values.email, values.password);
        if (!loginSecured) {
          enqueueSnackbar("Login credentials are incomplete", { variant: "error" });
          return;
        }

        const loginUrl = `${authxAppsBase()}/login`;
        const generateTokenResponse = await fetch(loginUrl, {
          method: "POST",
          headers: loginAuthxHeadersPreSession("127.0.0.2"),
          body: JSON.stringify({ loginHash: loginSecured }),
        });

        const loginBody = await parseAuthxAppsResponseBody(generateTokenResponse);
        const loginJson =
          typeof loginBody === "object" && loginBody ? loginBody : {};
        const sessionNorm = normalizeAuthxSession(loginJson);
        const verificationRedirect = getLoginVerificationRedirect(sessionNorm);

        if (
          !isAuthxLoginSessionUsable(
            generateTokenResponse.ok,
            sessionNorm,
            verificationRedirect,
          )
        ) {
          enqueueSnackbar(
            getAuthxAppsErrorMessage(loginBody, "Login failed"),
            { variant: "error" },
          );
          return;
        }

        if (verificationRedirect === "email") {
          persistAuthxSessionCookies(loginJson, values.email);
          persistPendingVerificationState(sessionNorm, loginSecured);
          navigate("/verify-email", {
            state: {
              firstName:
                sessionNorm.firstName || values.email.split("@")[0] || "",
              email: values.email,
              loginSecured: loginSecured,
              accessKey: sessionNorm.accessKey,
              refreshKey: sessionNorm.refreshKey,
            },
          });
        } else if (verificationRedirect === "phone") {
          persistAuthxSessionCookies(loginJson, values.email);
          persistPendingVerificationState(sessionNorm, loginSecured);
          trackLoginSuccess(values.email);
          navigate("/verify-phone", {
            state: {
              loginSecuredHash: loginSecured,
              phone: sessionNorm.mobPhoneNo,
              accessKey: sessionNorm.accessKey,
              refreshKey: sessionNorm.refreshKey,
            },
          });
        } else {
          trackLoginSuccess(values.email);
          navigate("/mfa", {
            state: {
              redirectBackTo: redirectBackTo,
              email: values.email,
              accessKey: sessionNorm.accessKey,
              refreshKey: sessionNorm.refreshKey,
              ip: "127.0.0.2",
              loginSecuredHash: loginSecured,
            },
          });
        }
      } catch (error) {
        console.log("4");
        console.error("Error:", error);
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const redirectUrl = urlParams.get("redirectBackTo");
    if (redirectUrl) {
      setRedirectBackTo(
        getSafeRedirectUrl(redirectUrl, window.location.origin),
      );
      urlParams.delete("redirectBackTo");
      window.history.replaceState(
        {},
        "",
        `${window.location.pathname}?${urlParams.toString()}`
      );
    }
  }, []);

  return (
    <Box className="glass-card" sx={{ p: { xs: 3.5, md: 4 }, width: "100%" }}>
      <Snackbar
        sx={{ zIndex: 9999999 }}
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        data-testid="login-panel--Snackbar-0"
      >
        <Alert
          onClose={handleClose}
          severity="error"
          variant="filled"
          sx={{ width: "100%" }}
          data-testid="login-panel--Alert-0"
        >
          {errorMessage}
        </Alert>
      </Snackbar>

      <Typography variant="h2" sx={{ fontWeight: 700, mb: 4, color: "text.primary" }}>
        Log in
      </Typography>

      <Box component="form" onSubmit={formik.handleSubmit}>
        <TextField
          autoComplete="username"
          fullWidth
          label="Email Address"
          name="email"
          value={formik.values.email}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.email && Boolean(formik.errors.email)}
          helperText={formik.touched.email ? formik.errors.email : ""}
          sx={{ mb: 2.5 }}
          data-testid="login-panel--TextField-0"
        />

        <TextField
          autoComplete="current-password"
          fullWidth
          type={showPassword ? "text" : "password"}
          label="Password"
          name="password"
          value={formik.values.password}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.password && Boolean(formik.errors.password)}
          helperText={formik.touched.password ? formik.errors.password : ""}
          sx={{ mb: 1.5 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                  sx={{ color: "text.secondary" }}
                  data-testid="login-panel--IconButton-0"
                >
                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              formik.handleSubmit();
            }
          }}
          data-testid="login-panel--TextField-1"
        />

        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}>
          <MuiLink
            component={Link}
            onClick={(e) => {
              e.preventDefault();
              trackButtonClick("Forgot Password");
              navigate("/reset-password");
            }}
            to="/reset-password"
            underline="hover"
            sx={{ color: "primary.main", fontSize: 13, fontWeight: 500 }}
            data-testid="login-panel--Link-0"
          >
            Forgot password?
          </MuiLink>
        </Box>

        <Button
          type="submit"
          variant="outlined"
          className="btn-glass"
          fullWidth
          disabled={formik.isSubmitting}
          sx={{ mb: 3 }}
          data-testid="login-panel--Button-0"
        >
          {formik.isSubmitting ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            "Log In"
          )}
        </Button>

        <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
          <ThemeToggle
            titlefontSize={16}
            title={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
          />
        </Box>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: "center", mb: 1.5 }}
        >
          Don&apos;t have an account?
        </Typography>
        <Button
          component={Link}
          to="/register"
          variant="outlined"
          className="btn-glass"
          fullWidth
          data-testid="login-panel--Link-1"
        >
          Sign Up
        </Button>
      </Box>
    </Box>
  );
};

export default LoginPanel;
